import {
  applyTurn,
  findAssaults,
  findEncounters,
  findSieges,
  isInEnemyTerritory,
  squareKey,
  toAlgebraic,
} from "../engine";
import type { Capture, GameState, Move, Piece, Side, Square } from "../engine";
import type { BotAction, MoveReview } from "../bot/minimax";
import type { GamePhase, TutorialReview } from "./script";

/** A single move played in the scripted game, with everything the narrator needs to describe it. */
export interface MoveSnapshot {
  pre: GameState;
  post: GameState;
  move: Move;
  declared?: Capture;
  advance?: boolean;
  sieges: Capture[];
  moveNumber: number;
  side: Side;
  /** Which game phase this move sits in — drives phase preambles + badges. */
  phase: GamePhase;
  /** True when this move marks a phase transition (turn 1, 9, 21, 33). */
  phaseTransition: boolean;
  /** First-of-kind flags help the narrator surface rule explanations only the first time a concept appears. */
  firstTriangleLeap: boolean;
  firstSquareMove: boolean;
  firstPyramidMove: boolean;
  firstCaptureOverall: boolean;
  firstCrossing: boolean;
  firstAssault: boolean;
  firstSiege: boolean;
}

export interface NarratedMove {
  title: string;
  narration: string[];
  highlights: string[];
  phase: GamePhase;
  /** Pre-move "here's the plan" sentence (commentator voice). Optional. */
  why?: string;
  /** Post-move forward-look. Optional. */
  watchFor?: string;
}

/** Map a 1-indexed move number to the game phase it belongs to. */
export function computePhase(moveNumber: number): GamePhase {
  if (moveNumber <= 8) return "opening";
  if (moveNumber <= 20) return "early-midgame";
  if (moveNumber <= 32) return "late-midgame";
  return "endgame";
}

/** One-sentence framing prepended to narration[0] at phase boundaries. */
const PHASE_PREAMBLE: Record<GamePhase, string> = {
  opening:
    "Opening phase — both sides develop their front-rank Rounds first. Captures are rare; positioning is everything.",
  "early-midgame":
    "Entering the early midgame — pieces are committed, captures start landing, and the value math comes into play.",
  "late-midgame":
    "Late midgame — armies thin, the Kings come under pressure, and tactical exchanges decide the game.",
  endgame:
    "Endgame — the triumph zone matters now. Whoever gets three or four matching-progression pieces into enemy ranks first wins.",
};

/** Short uppercase label used in highlight pills at phase transitions. */
const PHASE_BADGE: Record<GamePhase, string> = {
  opening: "Opening",
  "early-midgame": "Early midgame",
  "late-midgame": "Late midgame",
  endgame: "Endgame",
};

const SHAPE_LABEL: Record<string, string> = {
  round: "Round",
  triangle: "Triangle",
  square: "Square",
  pyramid: "Pyramid",
};

const SHAPE_VERB: Record<string, string> = {
  round: "advances",
  triangle: "moves",
  square: "slides",
  pyramid: "moves",
};

export function narrateMove(snap: MoveSnapshot): NarratedMove {
  const { side, moveNumber, move, declared, advance, sieges, pre } = snap;
  const sideLabel = side === "white" ? "White" : "Black";
  const piece = pre.board.get(squareKey(move.from));
  if (!piece) {
    // Defensive: a valid game would always have a piece on the from-square. If something's
    // off, return a minimal record so the panel still renders without crashing.
    return { title: `Move ${moveNumber}`, narration: [], highlights: [], phase: snap.phase, why: undefined, watchFor: undefined };
  }
  const pieceLabel = `${SHAPE_LABEL[piece.shape]}-${piece.value}`;
  const fromAlg = toAlgebraic(move.from);
  const toAlg = toAlgebraic(move.to);
  const modeNote = move.mode ? ` (as ${SHAPE_LABEL[move.mode]})` : "";
  const verb = SHAPE_VERB[piece.shape];

  const highlights: string[] = [];
  const narration: string[] = [];

  // 1) Lead — commentator third-person, like a chess broadcast.
  const phasePreface = snap.phaseTransition ? `${PHASE_PREAMBLE[snap.phase]} ` : "";
  narration.push(`${phasePreface}${sideLabel} ${verb} ${pieceLabel} from ${fromAlg} to ${toAlg}${modeNote}.`);

  // Phase badge — only shown on transition turns. Sits alongside the existing first-of-kind badges.
  if (snap.phaseTransition) {
    highlights.push(PHASE_BADGE[snap.phase]);
  }

  // 2) Rule explanations for first-of-kind movements (told once per game, when first observed)
  if (snap.firstTriangleLeap && (piece.shape === "triangle" || move.mode === "triangle")) {
    narration.push(
      "Triangles move two squares horizontally or vertically, with the path clear. They can also \"fly\" via a chess-knight's hop (one square one way, two the other) — that flying move leaps over any piece in between, but it can only be used to move into empty squares, never to capture.",
    );
    highlights.push("Triangle move");
  }
  if (snap.firstSquareMove && (piece.shape === "square" || move.mode === "square")) {
    narration.push(
      "Squares move three squares horizontally or vertically — the path must be clear. They also have an \"extended knight\" flying option (one square one way, three the other), which leaps but can't be used to capture.",
    );
    highlights.push("Square move");
  }
  if (snap.firstPyramidMove && piece.shape === "pyramid") {
    const pyramidNote =
      piece.side === "white"
        ? "White's King (Pyramid) carries six layers totaling 91 — Round-1, Round-4, Triangle-9, Triangle-16, Square-25, Square-36."
        : "Black's King (Pyramid) carries five layers totaling 190 — Round-16, Triangle-25, Triangle-36, Square-49, Square-64.";
    narration.push(
      `${pyramidNote} Each turn its owner declares whether it moves like a Round, Triangle, or Square — but the mode is only legal if at least one uncaptured layer carries that shape. Once every layer of a given shape is taken, the King loses that movement option.`,
    );
    highlights.push("Pyramid mode");
  }

  // 3) Territory crossing
  if (snap.firstCrossing && isInEnemyTerritory(side, move.to.rank)) {
    narration.push(
      `${pieceLabel} crosses the midline into ${side === "white" ? "Black" : "White"}'s half. The triumph zone — where pieces actually count toward Greater/Royal/Glorious — is stricter: the opponent's first five ranks (${side === "white" ? "ranks 12–16" : "ranks 1–5"}). The midline is a stepping stone.`,
    );
    highlights.push("Enemy territory");
  }

  // 4) Siege captures (auto-resolved)
  if (sieges.length > 0) {
    for (const s of sieges) {
      const target = pre.board.get(squareKey(s.target));
      if (!target) continue;
      const targetLabel = `${SHAPE_LABEL[target.shape]}-${target.value}`;
      narration.push(
        `Siege at ${toAlgebraic(s.target)}: ${target.side === "white" ? "White" : "Black"}'s ${targetLabel} is surrounded on all four orthogonal sides by ${sideLabel}'s pieces or board edges. Siege triggers automatically — no declaration needed.`,
      );
      highlights.push("Siege");
    }
    if (snap.firstSiege) {
      narration.push(
        "Siege is the only capture method that doesn't depend on arithmetic. Corner squares require just two surrounders (the board edges count as walls); the open board needs four.",
      );
    }
  }

  // 5) Declared capture (Encounter / Assault / Deceit)
  if (declared) {
    const target = pre.board.get(squareKey(declared.target));
    if (target) {
      const targetLabel = `${SHAPE_LABEL[target.shape]}-${target.value}`;
      const captureSentence = (() => {
        switch (declared.method) {
          case "encounter":
            return `${sideLabel} declares Encounter: captures ${targetLabel} at ${toAlgebraic(declared.target)}. Matching values + legal range: ${declared.formula}.${advance ? " Attacker advances into the vacated square." : ""}`;
          case "deceit":
            return `${sideLabel} declares Deceit: captures ${targetLabel} at ${toAlgebraic(declared.target)} via ${declared.formula} and advances into the vacated square.`;
          case "assault":
            return `${sideLabel} declares Assault: two attackers combine arithmetically (${declared.formula}) to capture ${targetLabel} at ${toAlgebraic(declared.target)}. Neither attacker moves.`;
          default:
            return `${sideLabel} captures ${targetLabel} at ${toAlgebraic(declared.target)} via ${declared.formula}.`;
        }
      })();
      narration.push(captureSentence);
      highlights.push(SHAPE_LABEL[declared.method] ?? capitalize(declared.method));

      if (snap.firstAssault && declared.method === "assault") {
        narration.push(
          "Assault is the deeper capture method: two of your pieces both in range of one enemy, and their values combine — by sum, difference, product, or integer-exact division — to equal the enemy's value. Both attackers stay where they are.",
        );
      }
    }
    if (snap.firstCaptureOverall) {
      narration.push(
        "First capture of the game. Captures matter on two paths: they push your tray total toward the Common/Proper Victory threshold, and — at the higher levels — they clear lanes (and strip the enemy King) so your pieces can arrange a progression in the opponent's first five ranks.",
      );
    }
  }

  // 6) Position-aware fallback. If nothing notable surfaced above, look at the actual
  // before/after threats on this piece and any threats the move creates against the enemy.
  if (narration.length === 1) {
    const enemy: Side = side === "white" ? "black" : "white";

    // Captures the enemy could have made against the moving piece at its OLD square.
    const oldThreats = capturesTargeting(snap.pre, enemy, move.from);
    // Captures the enemy can now make against the moving piece at its NEW square.
    const newThreats = capturesTargeting(snap.post, enemy, move.to);
    // Captures THIS player can now make (after the move) — i.e., threats the move creates.
    const myNewCaptures = allCaptures(snap.post, side);

    const stepped = side === "white" ? move.to.rank > move.from.rank : move.to.rank < move.from.rank;

    // Priority order: escape > new threat created > stayed-still > generic.
    if (oldThreats.length > 0 && newThreats.length === 0) {
      // Escaping a threat.
      const t = oldThreats[0];
      const attacker = describeAttacker(snap.pre, t);
      narration.push(
        `Steps out of danger. The ${attacker} could have captured this piece next turn via ${t.method === "siege" ? "Siege (it was on a side already pinned)" : t.method + (t.formula ? ` (${t.formula})` : "")}.`,
      );
    } else if (myNewCaptures.length > 0) {
      // Created a capture threat.
      const t = myNewCaptures[0];
      const target = snap.post.board.get(squareKey(t.target));
      if (target) {
        narration.push(
          `Opens a ${t.method} threat: ${describeAttacker(snap.post, t)} now in range of ${SHAPE_LABEL[target.shape]}-${target.value} at ${toAlgebraic(t.target)}${t.formula ? ` (${t.formula})` : ""}. Next turn could take it.`,
        );
      } else {
        narration.push(stepped ? "Pushes toward enemy territory and sets up future captures." : "Quiet repositioning move.");
      }
    } else if (oldThreats.length > 0 && newThreats.length > 0) {
      // Was threatened, still threatened — backwards isn't an escape, must be tactical.
      narration.push(
        `Still in line of attack — the move trades one threat for another and probably sets up a defender or a future Assault. Greedy play doesn't always pick the deepest line.`,
      );
    } else if (newThreats.length > 0) {
      // Walked into a threat. Risky / blundering greedy move.
      const t = newThreats[0];
      const attacker = describeAttacker(snap.post, t);
      narration.push(
        `Risky step — the ${attacker} is now in range and could capture next turn (${t.method}${t.formula ? `, ${t.formula}` : ""}). The greedy script doesn't always avoid these; the in-game bot looks ahead and would usually decline.`,
      );
    } else if (stepped) {
      narration.push("Pushes toward enemy territory. No threats either way this turn.");
    } else {
      // Genuinely a backward / sideways step with no threat context. Rare in the opening,
      // common mid-game when the greedy bot lacks deeper planning.
      narration.push(
        "Backs up without an obvious threat to escape. The greedy script that drives this tutorial only looks at the current turn — strong play would consider several moves ahead, but here it's hedging without a clear reason. A stronger engine would prefer a forward step.",
      );
    }
  }

  const title = `Move ${moveNumber} · ${sideLabel}'s ${pieceLabel}`;
  const why = computeWhy(snap);
  const watchFor = computeWatchFor(snap);
  return { title, narration, highlights, phase: snap.phase, why, watchFor };
}

/**
 * One-sentence pre-move intent in commentator voice. Returns undefined when no concrete framing
 * stands out — the panel hides the slot then. Operates on snap.pre / snap.post only (no new
 * engine calls beyond what allCaptures already does).
 */
function computeWhy(snap: MoveSnapshot): string | undefined {
  const { side, move, declared, pre, post } = snap;
  const enemy: Side = side === "white" ? "black" : "white";
  const sideLabel = side === "white" ? "White" : "Black";

  // Priority 1: declared capture — name the math.
  if (declared) {
    const target = pre.board.get(squareKey(declared.target));
    if (target) {
      const targetLabel = `${SHAPE_LABEL[target.shape]}-${target.value}`;
      const targetAlg = toAlgebraic(declared.target);
      if (declared.method === "encounter" || declared.method === "deceit") {
        return `Matching values to clear the ${targetLabel} at ${targetAlg} — ${declared.formula}.`;
      }
      if (declared.method === "assault") {
        return `Combining two attackers to take down the ${targetLabel} at ${targetAlg} — ${declared.formula}.`;
      }
    }
  }

  // Priority 2: created a fresh capture threat — the move "sets up" something next turn.
  const preThreats = new Set(allCaptures(pre, side).map(captureKey));
  const postThreats = allCaptures(post, side);
  const newCapture = postThreats.find((c) => !preThreats.has(captureKey(c)));
  if (newCapture) {
    const tgt = post.board.get(squareKey(newCapture.target));
    if (tgt) {
      return `Setting up a ${newCapture.method} against ${sideLabel === "White" ? "Black" : "White"}'s ${SHAPE_LABEL[tgt.shape]}-${tgt.value} at ${toAlgebraic(newCapture.target)} for next turn.`;
    }
  }

  // Priority 3: escaped an enemy capture threat the piece was facing on its old square.
  const oldThreats = capturesTargeting(pre, enemy, move.from);
  const newThreats = capturesTargeting(post, enemy, move.to);
  if (oldThreats.length > 0 && newThreats.length === 0) {
    const t = oldThreats[0];
    return `Stepping out of range — ${describeAttacker(pre, t)} would have taken this piece on the next turn.`;
  }

  // Priority 4: advanced into the triumph zone — score reminder.
  const movedFromInZone = isInGreaterVictoryAreaForSide(side, move.from.rank);
  const movedToInZone = isInGreaterVictoryAreaForSide(side, move.to.rank);
  if (!movedFromInZone && movedToInZone) {
    return `Pushing into the opponent's first five ranks — pieces here count toward Greater, Royal, and Glorious progressions.`;
  }

  return undefined;
}

/**
 * One-sentence post-move forward look. Returns undefined when no meaningful opponent threat
 * just appeared. Skips low-value targets (Round-3 or below) — keeps watch-for for genuine pressure.
 */
function computeWatchFor(snap: MoveSnapshot): string | undefined {
  const { side, pre, post } = snap;
  const enemy: Side = side === "white" ? "black" : "white";
  const enemyLabel = enemy === "white" ? "White" : "Black";

  // The enemy threats that didn't exist before this move.
  const preEnemyThreats = new Set(allCaptures(pre, enemy).map(captureKey));
  const postEnemyThreats = allCaptures(post, enemy);
  const fresh = postEnemyThreats.filter((c) => !preEnemyThreats.has(captureKey(c)));
  if (fresh.length === 0) return undefined;

  // Pick the highest-target-value fresh threat — that's the one to actually worry about.
  let pick: Capture | undefined;
  let pickTargetValue = -1;
  for (const c of fresh) {
    const tgt = post.board.get(squareKey(c.target));
    if (!tgt) continue;
    if (tgt.value > pickTargetValue) {
      pickTargetValue = tgt.value;
      pick = c;
    }
  }
  if (!pick) return undefined;
  // Skip trivial threats — a threat against a tiny piece (Round-2 / Round-3) is too small to
  // be useful coaching. The "watch for" slot is for genuine pressure on something worth
  // defending.
  if (pickTargetValue <= 3) return undefined;

  const target = post.board.get(squareKey(pick.target));
  if (!target) return undefined;
  return `Watch for: ${enemyLabel} can now threaten a ${pick.method} against the ${SHAPE_LABEL[target.shape]}-${target.value} at ${toAlgebraic(pick.target)} next turn.`;
}

/** Stable string key for a Capture, so we can diff "before" vs "after" capture sets. */
function captureKey(c: Capture): string {
  const attackers = c.attackers.map(squareKey).sort().join("|");
  return `${c.method}|${squareKey(c.target)}|${attackers}`;
}

/** Inline copy of the engine's greater-victory-area check; kept private so narrate doesn't need to import it. */
function isInGreaterVictoryAreaForSide(side: Side, rank: number): boolean {
  // White triumphs in ranks 11..15; black in 0..4. Same rule as engine/constants isInGreaterVictoryArea.
  if (side === "white") return rank >= 11;
  return rank <= 4;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Captures that `side` can resolve against the piece at `square` — i.e., does any of
 * side's pieces have a legal capture line ending at this square?
 */
function capturesTargeting(state: GameState, side: Side, square: Square): Capture[] {
  const key = squareKey(square);
  return allCaptures(state, side).filter((c) => squareKey(c.target) === key);
}

/** Every capture (encounter + assault + siege) currently available to `side`. */
function allCaptures(state: GameState, side: Side): Capture[] {
  return [
    ...findEncounters(state, side),
    ...findAssaults(state, side),
    ...findSieges(state, side),
  ];
}

/**
 * Build a one-line "engine assessment" for a played move, given the engine's review.
 * Falls back to a clean summary when the played move WAS the engine's choice.
 */
export function narrateEngineReview(
  pre: GameState,
  side: Side,
  played: BotAction,
  review: MoveReview,
): TutorialReview {
  if (review.wasBest) {
    return {
      classification: review.classification,
      wasBest: true,
      engineNote: "Engine assessment: this was the best move available.",
    };
  }

  // Find a concrete reason the engine's preferred move is better. Try, in order:
  //   1. Did the best move capture something this one didn't?
  //   2. Did the best move avoid putting our piece into enemy reach?
  //   3. Did the best move open a future capture threat?
  //   4. Did it advance further into enemy territory?
  // If none of those resolve, fall back to a generic eval-delta note.
  const enemy: Side = side === "white" ? "black" : "white";

  // What would the best move have looked like?
  let bestPostState: GameState;
  try {
    const r = applyTurn(pre, review.bestAction.move, {
      declared: review.bestAction.declared,
      advance: review.bestAction.advance,
    });
    bestPostState = r.state;
  } catch {
    bestPostState = pre;
  }

  // What would the played move have looked like?
  let playedPostState: GameState;
  try {
    const r = applyTurn(pre, played.move, {
      declared: played.declared,
      advance: played.advance,
    });
    playedPostState = r.state;
  } catch {
    playedPostState = pre;
  }

  const bestPieceLabel = pieceLabelAt(pre, played.move.from); // fallback if best uses different piece
  void bestPieceLabel; // currently unused — kept for future-richer prose

  // Reason 1: best move captured (or stripped a layer); played move didn't capture or captured less.
  const bestCaptured = capturedThisMove(pre, bestPostState, enemy);
  const playedCaptured = capturedThisMove(pre, playedPostState, enemy);
  if (bestCaptured && (!playedCaptured || bestCaptured.value > playedCaptured.value)) {
    const verb = playedCaptured ? "captured a larger piece" : "captured";
    return {
      classification: review.classification,
      wasBest: false,
      engineNote: `Engine assessment (${labelForClass(review.classification)}): ${describeBotMove(pre, review.bestAction)} would have ${verb} — the ${SHAPE_LABEL[bestCaptured.shape]}-${bestCaptured.value}.`,
    };
  }

  // Reason 2: best move escaped a threat that the played move left exposed.
  const playedExposes = newThreatsOnOurSide(pre, playedPostState, side);
  const bestExposes = newThreatsOnOurSide(pre, bestPostState, side);
  if (playedExposes.length > bestExposes.length) {
    const t = playedExposes[0];
    return {
      classification: review.classification,
      wasBest: false,
      engineNote: `Engine assessment (${labelForClass(review.classification)}): ${describeBotMove(pre, review.bestAction)} would have avoided leaving your piece in range of an enemy ${t.method}${t.formula ? ` (${t.formula})` : ""}.`,
    };
  }

  // Reason 3: best move opens a capture threat next turn that the played move doesn't.
  const bestThreats = allCaptures(bestPostState, side);
  const playedThreats = allCaptures(playedPostState, side);
  if (bestThreats.length > playedThreats.length) {
    const t = bestThreats[0];
    const tgt = bestPostState.board.get(squareKey(t.target));
    if (tgt) {
      return {
        classification: review.classification,
        wasBest: false,
        engineNote: `Engine assessment (${labelForClass(review.classification)}): ${describeBotMove(pre, review.bestAction)} would have set up a ${t.method} against the ${SHAPE_LABEL[tgt.shape]}-${tgt.value} at ${toAlgebraic(t.target)}.`,
      };
    }
  }

  // Reason 4: best move pushes deeper into enemy half than the played move.
  const bestForward = forwardness(pre, review.bestAction.move, side);
  const playedForward = forwardness(pre, played.move, side);
  if (bestForward > playedForward + 1) {
    return {
      classification: review.classification,
      wasBest: false,
      engineNote: `Engine assessment (${labelForClass(review.classification)}): ${describeBotMove(pre, review.bestAction)} would have pushed further into enemy ranks.`,
    };
  }

  // Generic fallback — name the delta so even without a specific motif the message is honest.
  return {
    classification: review.classification,
    wasBest: false,
    engineNote: `Engine assessment (${labelForClass(review.classification)}): the engine preferred ${describeBotMove(pre, review.bestAction)} by ${review.delta.toFixed(1)} eval points — a quieter positional improvement than this one.`,
  };
}

function labelForClass(c: MoveReview["classification"]): string {
  switch (c) {
    case "best": return "best";
    case "great": return "great";
    case "good": return "good";
    case "inaccuracy": return "inaccuracy";
    case "mistake": return "mistake";
    case "blunder": return "blunder";
  }
}

function pieceLabelAt(state: GameState, sq: Square): string {
  const p = state.board.get(squareKey(sq));
  return p ? `${SHAPE_LABEL[p.shape]}-${p.value}` : "piece";
}

function describeBotMove(pre: GameState, a: BotAction): string {
  const piece = pre.board.get(squareKey(a.move.from));
  const pieceLabel = piece ? `${SHAPE_LABEL[piece.shape]}-${piece.value}` : "the piece";
  const modeNote = a.move.mode ? ` (as ${SHAPE_LABEL[a.move.mode]})` : "";
  return `${pieceLabel} from ${toAlgebraic(a.move.from)} to ${toAlgebraic(a.move.to)}${modeNote}`;
}

/** Was a piece of the enemy taken on this single move (board diff)? Returns the captured piece's summary. */
function capturedThisMove(pre: GameState, post: GameState, enemy: Side): { shape: Piece["shape"]; value: number } | null {
  // Count pieces by enemy side. If post has fewer than pre, something was captured.
  let preCount = 0;
  let postCount = 0;
  for (const p of pre.board.values()) if (p.side === enemy) preCount++;
  for (const p of post.board.values()) if (p.side === enemy) postCount++;
  if (postCount >= preCount) return null;
  // Find the missing-from-post enemy piece by scanning pre.
  for (const [k, p] of pre.board) {
    if (p.side !== enemy) continue;
    if (!post.board.has(k)) return { shape: p.shape, value: p.value };
  }
  return null;
}

/** Captures the enemy can resolve against any of OUR pieces at the post-state. */
function newThreatsOnOurSide(pre: GameState, post: GameState, side: Side): Capture[] {
  void pre;
  const enemy: Side = side === "white" ? "black" : "white";
  return allCaptures(post, enemy);
}

/**
 * How far forward into enemy ranks does this move land? Larger = deeper.
 * White advances by INCREASING rank; Black advances by DECREASING rank.
 */
function forwardness(pre: GameState, m: Move, side: Side): number {
  const dr = m.to.rank - m.from.rank;
  void pre;
  return side === "white" ? dr : -dr;
}

/** Human-readable label for whichever piece(s) carry out the capture. */
function describeAttacker(state: GameState, c: Capture): string {
  const pieces = c.attackers
    .map((sq) => state.board.get(squareKey(sq)))
    .filter((p): p is Piece => !!p);
  if (pieces.length === 0) return "attacker";
  if (pieces.length === 1) {
    return `${SHAPE_LABEL[pieces[0].shape]}-${pieces[0].value} at ${toAlgebraic(c.attackers[0])}`;
  }
  return pieces.map((p, i) => `${SHAPE_LABEL[p.shape]}-${p.value} at ${toAlgebraic(c.attackers[i])}`).join(" + ");
}
